import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { StatusChip } from "@/components/ui/status-chip"
import { LocationTag } from "@/components/ui/location-tag"
import { TherapistAvatar } from "@/components/ui/therapist-avatar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { PageProps } from "@/types/inertia"

type Therapist = {
  id: number
  name: string
  email: string
  speciality_en: string
  status: string
  listed_at: string | null
  sites: { id: number; name: string }[]
  photo_url: string | null
}

type SiteOption = { id: number; name: string }

type Props = PageProps & {
  therapists: Therapist[]
  sites: SiteOption[]
  filters: { search?: string; status?: string; site_id?: string }
}

export default function TherapistsIndex() {
  const { props } = usePage<Props>()
  const { therapists, sites, filters } = props
  const flash = props.flash

  const [search, setSearch] = React.useState(filters.search ?? "")
  const [status, setStatus] = React.useState(filters.status ?? "")
  const [siteId, setSiteId] = React.useState(filters.site_id ?? "")
  const [removeId, setRemoveId] = React.useState<number | null>(null)
  const [removeName, setRemoveName] = React.useState("")

  function applyFilters() {
    router.get(
      "/admin/therapists",
      { search: search || undefined, status: status || undefined, site_id: siteId || undefined },
      { preserveState: true, replace: true },
    )
  }

  function handleRemove() {
    if (!removeId) return
    router.delete(`/admin/therapists/${removeId}`)
    setRemoveId(null)
  }

  return (
    <AdminShell>
      <Head title="Therapists">
        <meta name="description" content="Manage therapist profiles for Sanctuary Therapy Centre." />
        <meta property="og:title" content="Therapists" />
        <meta property="og:description" content="Manage therapist profiles for Sanctuary Therapy Centre." />
      </Head>

      <PageHeader
        title="Therapists"
        description="Manage practitioner profiles."
        actions={
          <Button asChild>
            <Link href="/admin/therapists/new">+ Add Therapist</Link>
          </Button>
        }
      />

      {flash?.notice && (
        <p className="mt-4 text-sm text-accent">{flash.notice}</p>
      )}
      {flash?.alert && (
        <p className="mt-4 text-sm text-danger-display">{flash.alert}</p>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-3">
        <Input
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applyFilters()}
          className="w-56"
        />
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-40">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="inactive">Inactive</option>
        </Select>
        <Select value={siteId} onChange={(e) => setSiteId(e.target.value)} className="w-44">
          <option value="">All locations</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </Select>
        <Button variant="secondary" onClick={applyFilters}>
          Filter
        </Button>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-hidden rounded-lg border border-hairline">
        <table className="w-full text-sm">
          <thead className="border-b border-hairline bg-surface">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Therapist</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted sm:table-cell">Speciality</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted md:table-cell">Location(s)</th>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Status</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted lg:table-cell">Listed</th>
              <th className="px-4 py-3 text-right font-medium text-ink-muted">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {therapists.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-muted">
                  No therapists found.
                </td>
              </tr>
            )}
            {therapists.map((t) => (
              <tr key={t.id} className="bg-white hover:bg-surface/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {t.photo_url ? (
                      <img
                        src={t.photo_url}
                        alt={t.name}
                        className="h-8 w-8 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <TherapistAvatar name={t.name} size="sm" />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-ink-display truncate">{t.name}</p>
                      <p className="text-xs text-ink-muted truncate">{t.email}</p>
                    </div>
                  </div>
                </td>
                <td className="hidden px-4 py-3 text-ink-body sm:table-cell">
                  {t.speciality_en || <span className="text-ink-muted">—</span>}
                </td>
                <td className="hidden px-4 py-3 md:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {t.sites.length === 0 && <span className="text-ink-muted">—</span>}
                    {t.sites.map((s) => (
                      <LocationTag key={s.id} name={s.name} />
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <StatusChip status={t.status as "active" | "pending" | "inactive"} />
                </td>
                <td className="hidden px-4 py-3 text-ink-muted lg:table-cell">
                  {t.listed_at
                    ? new Date(t.listed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                    : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button asChild variant="secondary" size="sm">
                      <Link href={`/admin/therapists/${t.id}/edit`}>Edit</Link>
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-danger-display hover:border-danger hover:bg-danger-faded"
                      onClick={() => { setRemoveId(t.id); setRemoveName(t.name) }}
                    >
                      Remove
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Remove confirmation dialog */}
      <Dialog open={removeId !== null} onOpenChange={(open) => !open && setRemoveId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove therapist?</DialogTitle>
            <DialogDescription>
              This will permanently remove <strong>{removeName}</strong> and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRemoveId(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRemove}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  )
}
